import { useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";

import { useAppSelector } from "~/app/store";
import { isAutostoreView } from "~/lib/helpers";
import {
  selectAutostoreGridIdOverride,
  selectHoldTypeFilters,
  selectSearchedBinIds
} from "~/redux/selectors/inventorySelectors";
import {
  selectThisWorkstation,
  selectWorkstationAutostoreGridId
} from "~/redux/selectors/workstationsSelectors";

import { useViewType } from "./useViewType";

/**
 * Shared logic used by getInventory and getInventoryFilterCounts so that the
 * two queries don't drift apart.
 */
export function useGetInventoryParams() {
  const { search } = useLocation();
  const isAutostoreInventoryView = isAutostoreView(search);
  const { variantId: variantIdParam, binNumber: binNumberParam } = useParams<{
    variantId?: string;
    binNumber?: string;
  }>();
  const { isHoldsPage } = useViewType();

  const searchedBinIds = useAppSelector(selectSearchedBinIds);
  const selectedHoldTypeFilters = useAppSelector(selectHoldTypeFilters);

  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const selectedGridIdFromRedux = useAppSelector(
    selectWorkstationAutostoreGridId
  );
  const autostoreGridOverride = useAppSelector(selectAutostoreGridIdOverride);
  const selectedAutostoreGridId = siteWorkstation
    ? selectedGridIdFromRedux
    : autostoreGridOverride;

  const getInventoryParams = useMemo(
    () => ({
      autostoreGridId: isAutostoreInventoryView
        ? selectedAutostoreGridId
        : undefined,
      binId: binNumberParam ? (searchedBinIds ?? undefined) : undefined,
      variantId: variantIdParam ? [variantIdParam] : undefined,
      // Use undefined (not false) to mean "no hold filter". Also true when a
      // hold-type filter is selected so filter counts stay in sync with the list.
      hasHold:
        isHoldsPage || selectedHoldTypeFilters.length > 0 ? true : undefined
    }),
    [
      binNumberParam,
      isAutostoreInventoryView,
      isHoldsPage,
      searchedBinIds,
      selectedAutostoreGridId,
      variantIdParam,
      selectedHoldTypeFilters.length
    ]
  );

  /**
   * Wait until these are resolved before firing the query to avoid omitting the
   * binId filter and returning unscoped results for the whole grid.
   */
  const isLoadingBinIds = !!binNumberParam && !searchedBinIds;

  return { getInventoryParams, isLoadingBinIds };
}
